import React from 'react';

const ViewDetailAppointment = ({ appointment, onClose }) => {
  if (!appointment) {
    return <div className="alert alert-info">No appointment selected for viewing</div>;
  }

  // Safe string conversion function
  const safeString = (value) => {
    if (value === null || value === undefined) return 'N/A';
    if (typeof value === 'object') return JSON.stringify(value);
    return String(value);
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    try {
      const date = new Date(dateString);
      if (isNaN(date.getTime())) return safeString(dateString);
      return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      });
    } catch (error) {
      return safeString(dateString);
    }
  };

  const formatDateTime = (dateTimeString) => {
    if (!dateTimeString) return 'N/A';
    try {
      const date = new Date(dateTimeString);
      if (isNaN(date.getTime())) return safeString(dateTimeString);
      return date.toLocaleString('en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch (error) {
      return safeString(dateTimeString);
    }
  };

  const formatTime = (timeString) => {
    if (!timeString) return 'N/A';
    return safeString(timeString);
  };

  const getStatusBadge = (status) => {
    const statusClasses = {
      'Confirmed': 'badge bg-success',
      'Pending': 'badge bg-warning text-dark',
      'Cancelled': 'badge bg-danger',
      'Completed': 'badge bg-info',
      'In Progress': 'badge bg-primary'
    };
    return statusClasses[status] || 'badge bg-secondary';
  };

  const handleClose = () => {
    if (onClose) {
      onClose();
    }
  };

  return (
    <div className="container-fluid p-0">
      <div className="row align-items-center mb-3">
        <div className="col">
          <h3>Appointment Details</h3>
        </div>
      </div>

      <div className="card">
        <div className="card-body">
          {/* Basic Information */}
          <div className="row mb-4">
            <div className="col-md-6">
              <label className="form-label fw-bold">Appointment ID</label>
              <p className="form-control-plaintext">{safeString(appointment.id)}</p>
            </div>
            <div className="col-md-6">
              <label className="form-label fw-bold">Internal Appointment ID</label>
              <p className="form-control-plaintext">{safeString(appointment.appointmentId)}</p>
            </div>
          </div>

          <div className="row mb-4">
            <div className="col-md-6">
              <label className="form-label fw-bold">Status</label>
              <div>
                <span className={getStatusBadge(appointment.status)}>
                  {safeString(appointment.status || 'Pending')}
                </span>
              </div>
            </div>
            <div className="col-md-6">
              <label className="form-label fw-bold">Appointment Type</label>
              <p className="form-control-plaintext">{safeString(appointment.appointmentType)}</p>
            </div>
          </div>

          <div className="row mb-4">
            <div className="col-md-6">
              <label className="form-label fw-bold">Accepted</label>
              <div>
                <span className={`badge ${appointment.isaccepted ? 'bg-success' : 'bg-secondary'}`}>
                  {appointment.isaccepted ? 'Yes' : 'No'}
                </span>
              </div>
            </div>
          </div>

          {/* Patient Information */}
          <div className="mb-4">
            <h4 className="mb-3">Patient Information</h4>
            <div className="row">
              <div className="col-md-6 mb-3">
                <label className="form-label fw-bold">Patient Name</label>
                <div className="d-flex align-items-center">
                  {appointment.patientAvatar && typeof appointment.patientAvatar === 'string' && (
                    <img 
                      src={appointment.patientAvatar} 
                      alt="Patient Avatar" 
                      className="rounded-circle me-2"
                      style={{ width: '40px', height: '40px' }}
                      onError={(e) => { e.target.style.display = 'none'; }}
                    />
                  )}
                  <span className="form-control-plaintext">{safeString(appointment.patientName)}</span>
                </div>
              </div>
              <div className="col-md-6 mb-3">
                <label className="form-label fw-bold">Date of Birth</label>
                <p className="form-control-plaintext">{formatDate(appointment.dateOfBirth)}</p>
              </div>
            </div>
            <div className="row">
              <div className="col-md-6 mb-3">
                <label className="form-label fw-bold">User ID</label>
                <p className="form-control-plaintext">{safeString(appointment.userid)}</p>
              </div>
              <div className="col-md-6 mb-3">
                <label className="form-label fw-bold">Meeting User ID</label>
                <p className="form-control-plaintext">{safeString(appointment.meetinguserid)}</p>
              </div>
            </div>
            <div className="row">
              <div className="col-12 mb-3">
                <label className="form-label fw-bold">Address</label>
                <p className="form-control-plaintext">{safeString(appointment.address)}</p>
              </div>
            </div>
          </div>

          {/* Doctor Information */}
          <div className="mb-4">
            <h4 className="mb-3">Doctor Information</h4>
            <div className="row">
              <div className="col-md-6 mb-3">
                <label className="form-label fw-bold">Doctor Name</label>
                <p className="form-control-plaintext">{safeString(appointment.doctorName)}</p>
              </div>
              <div className="col-md-6 mb-3">
                <label className="form-label fw-bold">Doctor Role</label>
                <p className="form-control-plaintext">{safeString(appointment.doctorRole)}</p>
              </div>
            </div>
          </div>

          {/* Appointment Schedule */}
          <div className="mb-4">
            <h4 className="mb-3">Schedule Information</h4>
            <div className="row">
              <div className="col-md-6 mb-3">
                <label className="form-label fw-bold">Date</label>
                <p className="form-control-plaintext">{formatDate(appointment.date)}</p>
              </div>
              <div className="col-md-6 mb-3">
                <label className="form-label fw-bold">Time</label>
                <p className="form-control-plaintext">
                  {formatTime(appointment.startTime)} - {formatTime(appointment.endTime)}
                </p>
              </div>
            </div>
          </div>

          {/* Timing Information */}
          <div className="mb-4">
            <h4 className="mb-3">Timing Details</h4>
            <div className="row">
              <div className="col-md-6 mb-3">
                <label className="form-label fw-bold">Check-in Time</label>
                <p className="form-control-plaintext">{formatDateTime(appointment.checkInTime)}</p>
              </div>
              <div className="col-md-6 mb-3">
                <label className="form-label fw-bold">Wait Time</label>
                <p className="form-control-plaintext">{safeString(appointment.waitTime || 0)} minutes</p>
              </div>
            </div>
            <div className="row">
              <div className="col-md-6 mb-3">
                <label className="form-label fw-bold">Consultation Duration</label>
                <p className="form-control-plaintext">{safeString(appointment.consultationDuration || 0)} minutes</p>
              </div>
            </div>
          </div>

          {/* Medical Information */}
          <div className="mb-4">
            <h4 className="mb-3">Medical Information</h4>
            <div className="row">
              <div className="col-12 mb-3">
                <label className="form-label fw-bold">Medical History</label>
                <div className="border rounded p-3 bg-light">
                  <p className="mb-0">{safeString(appointment.medicalHistory || 'No medical history available')}</p>
                </div>
              </div>
            </div>
            <div className="row">
              <div className="col-12 mb-3">
                <label className="form-label fw-bold">Notes</label>
                <div className="border rounded p-3 bg-light">
                  <p className="mb-0">{safeString(appointment.notes || 'No notes available')}</p>
                </div>
              </div>
            </div>
          </div>

          {/* System Information */}
          <div className="mb-4">
            <h4 className="mb-3">System Information</h4>
            <div className="row">
              <div className="col-md-6 mb-3">
                <label className="form-label fw-bold">Created At</label>
                <p className="form-control-plaintext">{formatDate(appointment.createdAt)}</p>
              </div>
              <div className="col-md-6 mb-3">
                <label className="form-label fw-bold">Updated At</label>
                <p className="form-control-plaintext">{formatDate(appointment.updatedAt)}</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ViewDetailAppointment;